/**
 * CORE-C GAP-002 — provenance on sight.
 *
 * ============================================================================
 * WHAT THIS IS
 * ============================================================================
 * Two separate things, deliberately kept apart in one file because their
 * separation is the whole design:
 *
 *   1. A REGISTRY tying a Passport fact group to the evidence class its OWNING
 *      capability already established. CORE-C hosts; it does not decide.
 *   2. A PRESENTATION map from a canonical class to approved human copy.
 *
 * A component names a fact group. It never names a class, and it never writes
 * a badge string. That is what stops "this table is user-editable, so it must
 * be an assertion" from becoming semantics.
 *
 * ============================================================================
 * WHAT THE RESOLVER MAY NOT DO
 * ============================================================================
 * It must not infer a class from the table a value came from, the component
 * rendering it, whether a user edited it, whether Catalyst touched the
 * workflow, a sample size, a confidence score, or the mere existence of a row.
 * Every entry below cites the authority that established the class, and a fact
 * group with no entry resolves to NULL — unknown stays unknown.
 *
 * ============================================================================
 * SP-05 HAS SIX CLASSES; ALL SIX NOW HAVE APPROVED COPY (OD-35, 2026-10-15)
 * ============================================================================
 * The founder's CORE-C distinction (2026-10-15, the seventh approved label):
 * "Calculated by OptiBay" is a DETERMINISTIC calculation from known inputs with
 * a defined formula (`derivation`); "Inferred by OptiBay" is a prediction,
 * estimate, hypothesis or model-derived judgement that is not directly measured
 * or deterministically established (`inference`). An inference shows its
 * supporting provenance and confidence/uncertainty where available. Neither
 * label implies independent verification. A class without approved copy would
 * render no badge — never a misleading one.
 *
 * OD-35 hard rule: an inference or an unverified claim is NEVER described as a
 * verified fact. "Verified" never appears without a named verifier, and only
 * for a real verification with evidence — no SP-05 class here is that, so no
 * label below says "Verified".
 */

/** The SP-05 vocabulary, complete. CORE-C renders it and does not redefine it. */
export type EvidenceClass =
  | 'assertion'
  | 'attestation'
  | 'derivation'
  | 'measurement'
  | 'external_report'
  | 'inference';

/** Classes with approved human-facing copy (2026-09-13; measurement + external report added by OD-35, 2026-10-15). */
export const PRESENTABLE_CLASSES: readonly EvidenceClass[] = [
  'assertion',
  'attestation',
  'derivation',
  'measurement',
  'external_report',
  'inference',
] as const;

export interface FactProvenanceRule {
  readonly evidenceClass: EvidenceClass;
  /** The authority that established the class. Required — a rule without one is a guess. */
  readonly authority: string;
  /**
   * Core-100 R5 (CORE-C F/Prov): WHO asserted, for an `assertion`. The approved copy for an assertion is
   * "Self-Reported" — true only when the person themselves made the claim. A manager's opening baseline or a coaching
   * note is an assertion by ANOTHER party, and calling it self-reported would misdescribe it in the most damaging
   * direction. So "Self-Reported" renders only for `asserter: 'subject'`; any other (or unstated) asserter has no
   * approved badge copy yet (recorded as an OD-35 copy item) and the section states the class in its own words.
   */
  readonly asserter?: 'subject' | 'other_party';
}

/**
 * Passport fact groups whose evidence class an owning authority has already
 * established.
 *
 * A fact group is the unit a badge qualifies. It is deliberately finer than a
 * section where a section mixes origins: `PassportDiagnosticCompetency` renders
 * a derivation beside figures whose class CORE-I never declared, so the score
 * and the calibration are their own groups and the rest of the card has no
 * entry at all.
 */
export const PASSPORT_FACT_PROVENANCE: Readonly<Record<string, FactProvenanceRule>> = {
  'references': {
    evidenceClass: 'assertion',
    asserter: 'subject',
    authority:
      "CORE-N: \"'Jane Smith - former manager' is the candidate's assertion that Jane is a " +
      'reference; it is not Jane\'s attestation about the candidate, and MUST NOT be labelled ' +
      'verified, confirmed or checked."',
  },
  'eligibility': {
    evidenceClass: 'assertion',
    asserter: 'subject',
    authority:
      'CORE-N: "SHALL treat eligibility answers as the person\'s assertions or preferences - ' +
      'never as verified determinations."',
  },
  'student-verification': {
    evidenceClass: 'attestation',
    authority:
      'The verification row is a school standing behind a fact about the person, carrying its ' +
      'own attributed author and timestamp. SP-05: "Attestation - a fact an authorised party ' +
      'stands behind from direct observation or relationship." The lifecycle (including ' +
      'revocation) stays the owner\'s; CORE-C reads its status and does not redefine it.',
  },
  'performance-dna-efficiency': {
    evidenceClass: 'derivation',
    authority:
      'CORE-G Outputs: "Efficiency as displayed evidence | Passport - analytics - coaching | ' +
      'Durable | Derivation." This is the KPI-derived figure the Passport shows - ' +
      'useDirectTechnicianMetrics maps it from the kpi_passport_summary RPC field efficiency_ratio ' +
      '- and NOT the roster value on the technicians row, which carries its own separate ' +
      'provenance (manual / learned / imported) and is not rendered on the Passport at all. ' +
      'Same word, different fact; the two must not borrow one another class labels.',
  },
  'diagnostic-competency-score': {
    evidenceClass: 'derivation',
    authority:
      'CORE-I Outputs: "Derived diagnostic competency + sample basis | Passport (both ' +
      'audiences) - assignment as a separate signal | Durable | Derivation."',
  },
  'diagnostic-calibration': {
    evidenceClass: 'derivation',
    authority: 'CORE-I Outputs: "Calibration | Passport - CORE-H coaching | Durable | Derivation."',
  },

  // ── Core-100 R5 (CORE-C F/Prov) — the sections that rendered with no class ─────────────────────────────────────
  // Category proficiency is classed PER ROW: the portable record carries `provenance_class` at rest (one writer since
  // 20270206011900, `__project_portable_proficiency`), and the live shop path reads the same rule from CORE-E's
  // authority (`classifyLiveProficiency`). The class picks ONE of these groups; `unrecorded` picks none.
  'proficiency-demonstrated': {
    evidenceClass: 'derivation',
    authority:
      'CORE-E Outputs: "Authoritative demonstrated proficiency | ... | Durable | Derivation" and "Portable proficiency | ' +
      'CORE-B Passport, CORE-R Connect | Durable | Derivation." The portable writer marks a value demonstrated only when ' +
      'demonstrated evidence stands behind it (demonstrated_evidence_count > 0).',
  },
  'proficiency-manager-baseline': {
    evidenceClass: 'assertion',
    asserter: 'other_party',
    authority:
      'CORE-E Inputs: "Manual baseline | Manager / operator | Owns the value | Assertion by an authorised actor", and ' +
      '"MANAGER_ASSERTED | somebody at this shop stated it as a starting point | Assertion". CORE-E SHALL: "MUST NOT ' +
      'present a manually-set value as demonstrated."',
  },
  'proficiency-passport-carried': {
    evidenceClass: 'assertion',
    asserter: 'other_party',
    authority:
      'CORE-E: "PASSPORT_CARRIED | it came with the person from their own Passport on joining | Assertion, carried." ' +
      'Who originally stated it is not recorded, so it is not presented as self-reported either.',
  },
  'future-insight-evidence': {
    evidenceClass: 'derivation',
    authority: 'CORE-H Outputs: "Evidence statement | The person; management | Derived | Derivation."',
  },
  'future-insight-interpretation': {
    evidenceClass: 'inference',
    authority:
      'CORE-H Outputs: "Interpretation, marked as such | The person; management | Derived | Inference." Badge ' +
      '"Inferred by OptiBay" (founder, 2026-10-15) beside "What it may mean"; the evidence line states its sample.',
  },
  'pensieve-learning': {
    evidenceClass: 'inference',
    authority:
      'CORE-H Inputs: "Learned norms (Pensieve) | Catalyst | Consumes - optional | Inference"; likewise CORE-AB ' +
      '("Expected duration for this technician | Catalyst Pensieve | Inference") and CORE-AH ("Learned job/vehicle norms ' +
      '(Pensieve) | Catalyst | Inference"). Read from pensieve_vehicle_job_stats. Badge "Inferred by OptiBay" (founder, 2026-10-15).',
  },
  'coaching-note': {
    evidenceClass: 'assertion',
    asserter: 'other_party',
    authority:
      'CORE-H Inputs: "Manager coaching note | A human | Consumes | Assertion - must stay labelled"; Outputs: "Human ' +
      'coaching note, attributed". PassportCoachingNotes attributes each note to its shop and author in words.',
  },
};

/**
 * The class an owning authority established for this fact group, or NULL.
 *
 * NULL is the honest answer for everything not listed, and it is the common
 * case: most Passport facts reach CORE-C with no class at all, and several
 * owning capabilities require a per-record distinction their tables cannot yet
 * express. Recorded as dependencies, not guessed at here.
 */
export function resolveFactProvenance(factGroupId: string): FactProvenanceRule | null {
  return PASSPORT_FACT_PROVENANCE[factGroupId] ?? null;
}

export interface ProvenancePresentation {
  /** The visible badge text. */
  readonly label: string;
  /** Longer copy for a title/tooltip. Never contradicts the label. */
  readonly description: string;
  /** Carried through for tests and styling; never rendered raw. */
  readonly evidenceClass: EvidenceClass;
}

/**
 * Is this string a usable human-readable attribution?
 *
 * "Do not manufacture a source from an ID." A school whose name did not come
 * back must fall to the generic wording rather than have a UUID or a bare
 * number presented to a person as the institution that verified them.
 */
export function isUsableAttribution(source: unknown): source is string {
  if (typeof source !== 'string') return false;
  const s = source.trim();
  if (s.length < 2) return false;
  // A UUID, or anything that is only hex/digits/separators, is an identifier.
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s)) return false;
  if (/^[0-9a-f\s._:-]+$/i.test(s)) return false;
  return true;
}

/**
 * Canonical class -> approved presentation, or NULL when there is no approved
 * copy for it.
 *
 * The approved wording (2026-09-13, revised by founder decision OD-35,
 * 2026-10-15):
 *
 *   assertion       -> "Self-Reported" (the person's own claim; never "verified")
 *   attestation     -> "Confirmed by {issuer}" (an issuer's explicit statement,
 *                      e.g. a school), or "Asserted by Third Party" when no
 *                      usable issuer label is available. An attestation is not
 *                      a verification with evidence, so it is never "Verified".
 *   derivation      -> "Calculated by OptiBay" (deterministic: known inputs, defined formula)
 *   inference       -> "Inferred by OptiBay" (a prediction / estimate / model judgement; founder, 2026-10-15)
 *   measurement     -> "Recorded Measurement"
 *   external_report -> "Reported by External Source"
 *
 * `derivation` deliberately says CALCULATED. It is not AI, not a measurement,
 * not a human attestation and not an outside verification, and the copy must
 * not let a reader infer any of those.
 */
export function presentEvidenceClass(
  evidenceClass: EvidenceClass | null | undefined,
  attributedTo?: unknown,
): ProvenancePresentation | null {
  switch (evidenceClass) {
    case 'assertion':
      return {
        label: 'Self-Reported',
        description:
          'Stated by the person themselves. OptiBay has not verified it, and neither has a ' +
          'shop or an institution.',
        evidenceClass,
      };

    case 'attestation':
      return isUsableAttribution(attributedTo)
        ? {
            label: `Confirmed by ${attributedTo.trim()}`,
            description:
              `${attributedTo.trim()} confirmed this from direct knowledge of the person. OptiBay has ` +
              'not independently verified it.',
            evidenceClass,
          }
        : {
            label: 'Asserted by Third Party',
            description:
              'A third party stated this. Their name is not available to show here, and OptiBay has ' +
              'not independently verified it.',
            evidenceClass,
          };

    case 'derivation':
      return {
        label: 'Calculated by OptiBay',
        description:
          'OptiBay calculated this from recorded work with a defined formula. It is not an estimate, ' +
          'it was not stated by a person, measured by a tool or supplied by an outside system, and ' +
          'it has not been independently verified.',
        evidenceClass,
      };

    case 'measurement':
      return {
        label: 'Recorded Measurement',
        description:
          'Captured directly as the work happened (for example by the work timer). It was not ' +
          'estimated, and it was not stated by a person.',
        evidenceClass,
      };

    case 'external_report':
      return {
        label: 'Reported by External Source',
        description:
          'Supplied by an identifiable outside system. OptiBay shows it as reported and has not ' +
          'independently verified it.',
        evidenceClass,
      };

    // Founder decision 2026-10-15 (CORE-C): the seventh approved label.
    case 'inference':
      return {
        label: 'Inferred by OptiBay',
        description:
          'OptiBay estimated or predicted this from patterns in recorded work. It is not a measurement ' +
          'or a fixed calculation, it may be wrong, and it has not been verified. The basis and how much ' +
          'evidence it rests on are shown with it.',
        evidenceClass,
      };

    default:
      // Unknown, absent, or a value this build does not recognise. Never
      // coerced to the nearest known class.
      return null;
  }
}

/** Convenience: registry lookup and presentation in one step. */
export function presentFactProvenance(
  factGroupId: string,
  attributedTo?: unknown,
): ProvenancePresentation | null {
  const rule = resolveFactProvenance(factGroupId);
  if (!rule) return null;
  // "Self-Reported" is approved copy for the person's OWN assertion only (see `asserter`).
  if (rule.evidenceClass === 'assertion' && rule.asserter !== 'subject') return null;
  return presentEvidenceClass(rule.evidenceClass, attributedTo);
}
